import { Box, Divider, Group, Stack, Text, Tooltip } from "@mantine/core";
import {
  PaciHealthIncident,
  PaciHealthStatus,
  usePaciHealth,
} from "../hooks/usePaciHealth";

type StatusMeta = {
  label: string;
  description: string;
  color: string;
};

const STATUS_META: Record<PaciHealthStatus, StatusMeta> = {
  healthy: {
    label: "OK",
    description: "No active PACI Monitoring incidents.",
    color: "#22c55e",
  },
  degraded: {
    label: "Degraded",
    description: "PACI has active non-critical alerts.",
    color: "#eab308",
  },
  down: {
    label: "Down",
    description: "PACI has active critical alerts.",
    color: "#ef4444",
  },
  unknown: {
    label: "Unknown",
    description: "PACI health has not reported yet.",
    color: "#9ca3af",
  },
};

const formatTimestamp = (timestamp: { toDate: () => Date } | null) => {
  if (!timestamp) {
    return "Not recorded";
  }

  return timestamp.toDate().toLocaleString("en-GB", { hour12: true });
};

const formatIsoTime = (value?: string) => {
  if (!value) {
    return "Not recorded";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-GB", { hour12: true });
};

const incidentTitle = (incident: PaciHealthIncident) => (
  incident.policyDisplayName ?? incident.conditionDisplayName ?? incident.id ?? "PACI alert"
);

const severityColor = (severity?: PaciHealthIncident["severity"]) => (
  severity === "down" ? STATUS_META.down.color : STATUS_META.degraded.color
);

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" gap="sm" wrap="nowrap">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="xs" fw={600} ta="right">
        {value}
      </Text>
    </Group>
  );
}

function IncidentDetail({ incident }: { incident: PaciHealthIncident }) {
  return (
    <Box
      style={{
        borderTop: "1px solid #e5e7eb",
        paddingTop: 6,
      }}
    >
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="xs" fw={700} lineClamp={1}>
          {incidentTitle(incident)}
        </Text>
        {incident.severity && (
          <Text size="xs" fw={700} c={severityColor(incident.severity)}>
            {incident.severity === "down" ? "Critical" : "Degraded"}
          </Text>
        )}
      </Group>
      {incident.conditionDisplayName && (
        <Text size="xs" c="dimmed" lineClamp={2}>
          {incident.conditionDisplayName}
        </Text>
      )}
      {incident.summary && (
        <Text size="xs" lineClamp={3}>
          {incident.summary}
        </Text>
      )}
      <Text size="xs" c="dimmed">
        Observed: {formatIsoTime(incident.observedAt)}
      </Text>
    </Box>
  );
}

export function PaciHealthPill() {
  const {
    status,
    message,
    activeIncidentCount,
    activeIncidents,
    updatedAt,
    lastEventAt,
    lastNotification,
    loading,
    error,
  } = usePaciHealth();

  const meta = STATUS_META[status];
  const displayLabel = loading ? "Loading" : meta.label;
  const detailText = loading
    ? "checking..."
    : activeIncidentCount > 0
      ? `${activeIncidentCount} alert${activeIncidentCount === 1 ? "" : "s"}`
      : "";

  const tooltipLabel = (
    <Stack gap={6} maw={360}>
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="sm" fw={700}>
          PACI health
        </Text>
        <Text size="xs" fw={700} c={meta.color}>
          {displayLabel}
        </Text>
      </Group>
      <Text size="xs">{message || meta.description}</Text>
      {error?.message && (
        <Text size="xs" c={STATUS_META.down.color}>
          {error.message}
        </Text>
      )}
      <Divider />
      <Stack gap={3}>
        <DetailRow label="Active alerts" value={String(activeIncidentCount)} />
        <DetailRow label="Updated" value={formatTimestamp(updatedAt)} />
        <DetailRow label="Last event" value={formatTimestamp(lastEventAt)} />
        {lastNotification?.state && (
          <DetailRow label="Last notification" value={lastNotification.state} />
        )}
        {lastNotification?.policyDisplayName && (
          <DetailRow label="Last policy" value={lastNotification.policyDisplayName} />
        )}
      </Stack>
      {activeIncidents.slice(0, 3).map((incident, index) => (
        <IncidentDetail
          key={incident.id ?? `${incidentTitle(incident)}-${index}`}
          incident={incident}
        />
      ))}
      {activeIncidents.length > 3 && (
        <Text size="xs" c="dimmed">
          {activeIncidents.length - 3} more active alert{activeIncidents.length - 3 === 1 ? "" : "s"}.
        </Text>
      )}
    </Stack>
  );

  return (
    <Tooltip label={tooltipLabel} withArrow position="bottom-end" multiline w={380}>
      <Box
        aria-label={`PACI health: ${displayLabel}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          minWidth: 92,
          padding: "4px 10px",
          borderRadius: 999,
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
          lineHeight: 1,
        }}
      >
        <Box
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: meta.color,
          }}
        />
        <Text size="xs" fw={600} c="#334155">
          PACI
        </Text>
        <Text size="xs" fw={700} c={meta.color}>
          {displayLabel}
        </Text>
        {detailText && (
          <Text size="xs" c="#6b7280">
            {detailText}
          </Text>
        )}
      </Box>
    </Tooltip>
  );
}
